import {
  ArrowLeftRight,
  BanknoteCheck,
  Gavel,
  Groceries,
  MessageCircleMore,
  Send,
  Settings,
  ShieldUser,
  ShoppingCart,
  Summary,
  Tag,
  UserCog,
  UserGroup,
  Users,
  UserShield,
  Warehouse,
} from "lucide-react";

import { isExternalUser, isMCFAdministrator, isSystemAdministrator } from "../../utils/roles";
import type { NavigationUser } from "../NavigationUserMenu/types";
import { isItemActive } from "../SidebarNavigation";
import type {
  ActiveTrail,
  ManagerNavigation,
  ManagerNavigationItem,
  ManagerNavigationSection,
} from "./types";

const overviewItem: ManagerNavigationItem = {
  labelKey: "header.overview",
  to: "/wfpManager",
  icon: Summary,
  exact: true,
  isRestricted: true,
  isExternalUserVisible: true,
};

const navigationSections: ManagerNavigationSection[] = [
  {
    id: "systemRoles",
    labelKey: "header.systemRoles",
    icon: UserGroup,
    items: [
      { labelKey: "header.farmers", to: "/wfpManager/farmers", icon: Users, isRestricted: true },
      {
        labelKey: "header.aggregators",
        to: "/wfpManager/aggregators",
        icon: Warehouse,
        isRestricted: true,
        isExternalUserVisible: true,
      },
      {
        labelKey: "header.buyers",
        to: "/wfpManager/traders",
        icon: ShoppingCart,
        isRestricted: true,
        isExternalUserVisible: true,
      },
      {
        labelKey: "header.managers",
        to: "/wfpManager/managers",
        icon: UserCog,
        isRestricted: true,
      },
      {
        labelKey: "header.externalUsers",
        to: "/wfpManager/externalUsers",
        icon: UserShield,
        isRestricted: true,
      },
    ],
  },
  {
    id: "marketAccess",
    labelKey: "header.marketAccess",
    icon: ArrowLeftRight,
    items: [
      {
        labelKey: "header.forSale",
        to: "/wfpManager/market",
        icon: Tag,
        isRestricted: true,
        isExternalUserVisible: true,
      },
      {
        labelKey: "header.offerView",
        to: "/wfpManager/offers",
        icon: BanknoteCheck,
        isRestricted: true,
        isExternalUserVisible: true,
      },
      {
        labelKey: "header.bidRequests",
        to: "/wfpManager/bidRequests",
        icon: Gavel,
        isRestricted: true,
        isExternalUserVisible: true,
      },
      {
        labelKey: "header.commodities",
        to: "/wfpManager/commodities",
        icon: Groceries,
        isRestricted: true,
      },
    ],
  },
  {
    id: "services",
    labelKey: "header.services",
    icon: Send,
    items: [
      {
        labelKey: "header.infoHub",
        to: "/wfpManager/infohub",
        icon: MessageCircleMore,
        isExternalUserVisible: true,
      },
    ],
  },
  {
    id: "administration",
    labelKey: "header.administration",
    icon: UserCog,
    items: [
      {
        labelKey: "header.settings",
        to: "/wfpManager/configuration",
        icon: Settings,
        isRestricted: true,
      },
      {
        labelKey: "header.platformAccess",
        to: "/systemAdmin/platformAccess",
        icon: ShieldUser,
        isSystemAdminOnly: true,
      },
    ],
  },
];

const hasLimitedNavigationAccess = (user?: NavigationUser) => {
  if (!user?.user_groups?.length) {
    return true;
  }

  const userData = { user_groups: user.user_groups };

  if (isSystemAdministrator(userData)) {
    return false;
  }

  return isExternalUser(userData) || isMCFAdministrator(userData);
};

export const getVisibleNavigation = (user?: NavigationUser): ManagerNavigation => {
  const userData = { user_groups: user?.user_groups ?? [] };
  const shouldHideRestrictedItems = hasLimitedNavigationAccess(user);
  const isSystemAdmin = isSystemAdministrator(userData);
  const isExternal = isExternalUser(userData);

  const isVisible = ({
    isExternalUserVisible,
    isRestricted,
    isSystemAdminOnly,
  }: ManagerNavigationItem) => {
    if (isExternal) {
      return !!isExternalUserVisible;
    }

    return (!isRestricted || !shouldHideRestrictedItems) && (!isSystemAdminOnly || isSystemAdmin);
  };

  return {
    overview: isVisible(overviewItem) ? overviewItem : undefined,
    sections: navigationSections
      .map(section => ({ ...section, items: section.items.filter(isVisible) }))
      .filter(({ items }) => items.length > 0),
  };
};

export const getActiveTrail = (
  pathname: string,
  { overview, sections }: ManagerNavigation
): ActiveTrail => {
  if (overview && isItemActive(pathname, overview)) {
    return { item: overview };
  }

  for (const section of sections) {
    const item = section.items.find(sectionItem => isItemActive(pathname, sectionItem));

    if (item) {
      return { section, item };
    }
  }

  return {};
};
